import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { SelectorCuentaPago } from '../components/CuentaPago';
import { Spinner, Toast, css, PageHeader, Modal, Label, Nixie } from '../components/UI';

// Cierre de pagos: lo que ya debió pagarse y no ha quedado cerrado (pagado con
// su comprobante adjunto). La regla vive en el backend (utils/cierre.js) para que
// esta pantalla, la tarjeta del Dashboard, el contador del menú y el MCP cuenten
// exactamente lo mismo.

const BASE = { solicitud: '/solicitudes', reembolso: '/reembolsos', finiquito: '/finiquitos', comprobacion: '/comprobaciones' };
const CASOS = [
  { id: 'vencida_sin_comprobante', titulo: 'Fecha de pago vencida', ayuda: 'Ya pasó su fecha programada y no tiene comprobante: probablemente se pagó en el banco y falta registrarlo.', accion: 'Registrar pago' },
  { id: 'comprobante_sin_marcar', titulo: 'Comprobante sin marcar pagada', ayuda: 'El comprobante ya se subió, pero la solicitud sigue en "por pagar".', accion: 'Marcar pagada' },
  { id: 'pagada_sin_comprobante', titulo: 'Pagada sin comprobante', ayuda: 'Se marcó pagada antes de que el comprobante fuera obligatorio (15 jul 2026). Falta adjuntarlo.', accion: 'Subir comprobante' },
];
const TIPO_LABEL = {
  pago: 'Gastos en General', debito: 'Débito', contribuciones: 'Contribuciones', activos_fijos: 'Activos Fijos',
  mercancias_nacionales: 'Mercancías Nac.', nomina: 'Nómina', caja_chica: 'Caja Chica', reembolso_gastos: 'Reembolso de Gastos',
  complemento_importacion: 'Complemento Importación', reembolso: 'Reembolso', finiquito: 'Finiquito', comprobacion: 'Comprobación',
};

const fmt = (n) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtEntero = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-MX')}`;
const fmtDia = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const hoyISO = () => new Date().toLocaleDateString('en-CA');
const dias = (n) => (n == null ? '—' : `${n} ${n === 1 ? 'día' : 'días'}`);
const pagos = (n) => `${n} ${n === 1 ? 'pago' : 'pagos'}`;

export default function CierrePagos() {
  const { user } = useAuth();
  const puedePagar = ['admin', 'programacion'].includes(user?.rol);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caso, setCaso] = useState('');
  const [modal, setModal] = useState(null); // { item, files, fecha, enviando }
  const [reenviando, setReenviando] = useState(null);
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4500); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await api.get('/indicadores/cierre')); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visibles = useMemo(() => (data?.pendientes || []).filter(p => !caso || p.caso === caso), [data, caso]);

  const abrir = (item) => setModal({ item, files: [], fecha: '', cuentaPago: '', enviando: false });

  const confirmar = async () => {
    const { item, files, fecha } = modal;
    const base = BASE[item.coleccion];
    const necesitaArchivo = item.caso !== 'comprobante_sin_marcar';
    const marcaPagada = item.estado === 'por_pagar';
    if (necesitaArchivo && !files.length) { toast_('Adjunta el comprobante de pago (PDF o imagen)', 'error'); return; }
    if (marcaPagada && !fecha) { toast_('Indica la fecha en que se hizo el pago', 'error'); return; }
    if (marcaPagada && fecha > hoyISO()) { toast_('La fecha de pago no puede ser futura', 'error'); return; }
    setModal(m => ({ ...m, enviando: true }));
    try {
      if (files.length) {
        const fd = new FormData();
        files.forEach(f => fd.append('archivos', f));
        fd.append('concepto', 'Comprobante de pago');
        await api.upload(`${base}/${item.id}/comprobantes`, fd);
      }
      if (marcaPagada) await api.patch(`${base}/${item.id}/estado`, { estado: 'pagada', fechaPago: fecha, cuentaPago: modal.cuentaPago || undefined });
      toast_(marcaPagada ? `${item.folio} quedó pagada y cerrada. Se avisará al solicitante por correo.` : `${item.folio} ya tiene su comprobante.`);
      setModal(null);
      load();
    } catch (e) {
      toast_(e.message, 'error');
      setModal(m => ({ ...m, enviando: false }));
    }
  };

  const reenviar = async (a) => {
    setReenviando(a.id);
    try {
      const r = await api.post(`/indicadores/aviso-pago/${a.coleccion}/${a.id}`, {});
      toast_(`Aviso de ${a.folio} enviado a ${r.email}`);
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setReenviando(null); }
  };

  const r = data?.resumen;
  const t = data?.tiempos;
  const meta = CASOS.find(c => c.id === modal?.item?.caso);

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader title="Cierre de pagos"
        subtitle="Un pago queda cerrado cuando está pagado y tiene su comprobante adjunto. Aquí está lo que falta."
        actions={<button type="button" onClick={load} disabled={loading} style={{ ...css.btnGhost, fontSize: 13 }}>Actualizar</button>} />

      {loading && !data && <Spinner />}
      {error && (
        <div role="alert" style={{ ...css.card, padding: 16, color: 'var(--danger)', display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
          <span>No se pudo cargar el cierre de pagos: {error}</span>
          <button type="button" onClick={load} style={css.btnGhost}>Reintentar</button>
        </div>
      )}

      {r && (
        <>
          {/* ── Estado general ── */}
          {r.todoCerrado ? (
            <div role="status" style={{ ...css.card, padding: '22px 24px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 16 }}>
              <span aria-hidden="true" style={{ fontSize: 28, color: 'var(--ok)' }}>✓</span>
              <div>
                <div style={{ fontFamily: 'var(--label-font)', fontSize: 20, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>Todos los pagos están cerrados</div>
                <div style={{ fontSize: 13, color: 'var(--glass)', marginTop: 4 }}>No hay pagos sin comprobante ni fechas de pago vencidas sin registrar.</div>
              </div>
            </div>
          ) : (
            <div style={{ ...css.card, padding: '18px 22px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
              <Nixie value={String(r.pendientes)} size={40} label={`${r.pendientes} pagos sin cerrar`} />
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontFamily: 'var(--label-font)', fontSize: 20, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>{r.pendientes === 1 ? 'Pago sin cerrar' : 'Pagos sin cerrar'}</div>
                <div style={{ fontSize: 13, color: 'var(--glass)', marginTop: 4 }}>
                  Suman <span style={{ fontFamily: 'var(--digit-font)', color: 'var(--ink)' }}>{fmt(r.montoMXN)}</span> MXN netos.
                  {' '}Una fecha programada cuenta como vencida a partir de {r.graciaDias} {r.graciaDias === 1 ? 'día' : 'días'} después.
                </div>
              </div>
            </div>
          )}

          {/* ── Casos: filtran la lista ── */}
          {!r.todoCerrado && (
            <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 18 }}>
              {CASOS.map(c => {
                const v = r.porCaso[c.id] || { folios: 0, montoMXN: 0 };
                const activo = caso === c.id;
                return (
                  <button key={c.id} type="button" aria-pressed={activo} disabled={!v.folios}
                    onClick={() => setCaso(activo ? '' : c.id)}
                    style={{
                      ...css.card, textAlign: 'left', padding: '14px 16px', cursor: v.folios ? 'pointer' : 'default', font: 'inherit', color: 'inherit',
                      borderColor: activo ? 'var(--plasma)' : 'var(--mesh)', background: activo ? 'var(--plasma-dim)' : 'var(--panel)',
                      opacity: v.folios ? 1 : 0.6,
                    }}>
                    <div className="nx-label" style={{ marginBottom: 8 }}>{c.titulo}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ fontFamily: 'var(--digit-font)', fontSize: 26, color: v.folios ? 'var(--ink)' : 'var(--glass)' }}>{v.folios}</span>
                      <span style={{ fontFamily: 'var(--digit-font)', fontSize: 13, color: 'var(--ink-soft)' }}>{fmtEntero(v.montoMXN)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 6, lineHeight: 1.4 }}>{c.ayuda}</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Lista ── */}
          {!r.todoCerrado && (
            <div style={{ ...css.card, padding: 0, overflow: 'hidden', marginBottom: 18 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--mesh-soft)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--label-font)', fontSize: 15, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>
                  {caso ? CASOS.find(c => c.id === caso).titulo : 'Todos los pendientes'} · {pagos(visibles.length)}
                </span>
                {caso && <button type="button" onClick={() => setCaso('')} style={{ ...css.btnGhost, fontSize: 12.5 }}>Ver todos</button>}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Folio', 'Empresa', 'Pagado a', 'Solicitante', 'Monto neto', 'Fecha', 'Sin cerrar', puedePagar ? 'Acción' : ''].map((h, i) => (
                        <th key={h || i} scope="col" style={{ padding: '9px 12px', textAlign: i === 4 ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map(p => {
                      const c = CASOS.find(x => x.id === p.caso);
                      const fecha = p.caso === 'pagada_sin_comprobante' ? `Pagada ${fmtDia(p.fechaPago)}` : `Programada ${fmtDia(p.fechaProgramacion)}`;
                      return (
                        <tr key={`${p.coleccion}-${p.id}`} style={{ borderBottom: '1px solid var(--mesh-soft)' }}>
                          <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                            <div style={{ fontFamily: 'var(--digit-font)', fontSize: 12, color: 'var(--plasma)' }}>{p.folio}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 2 }}>{TIPO_LABEL[p.tipo] || p.tipo}</div>
                          </td>
                          <td style={{ padding: '9px 12px' }}>{p.empresa}</td>
                          <td style={{ padding: '9px 12px', maxWidth: 220 }}>{p.proveedor || '—'}</td>
                          <td style={{ padding: '9px 12px', color: 'var(--ink-soft)' }}>{p.solicitante || '—'}</td>
                          <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--digit-font)', whiteSpace: 'nowrap' }}>{fmt(p.montoMXN)}</td>
                          <td style={{ padding: '9px 12px', color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>{fecha}</td>
                          <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', fontWeight: p.dias > 7 ? 700 : 400, color: p.dias > 7 ? 'var(--danger)' : 'var(--ink)' }}>
                            {dias(p.dias)}
                            {!caso && <div style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--glass)' }}>{c.titulo}</div>}
                          </td>
                          <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                            {puedePagar && (
                              <button type="button" onClick={() => abrir(p)} style={{ ...css.btn('#ff541c'), fontSize: 12.5, padding: '6px 12px', whiteSpace: 'nowrap' }}
                                aria-label={`${c.accion}: ${p.folio}`}>{c.accion}</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Tiempos de cierre ── */}
          {t && (
            <div style={{ ...css.card, padding: '16px 20px', marginBottom: 18 }}>
              <div style={{ fontFamily: 'var(--label-font)', fontSize: 15, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>Cuánto tardamos en cerrar</div>
              <div style={{ fontSize: 12.5, color: 'var(--glass)', marginBottom: 14 }}>
                {pagos(t.pagadas)} pagados del {fmtDia(t.periodo.desde)} al {fmtDia(t.periodo.hasta)}, por fecha real de pago.
              </div>
              <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
                <Metrica etiqueta="Del pago al comprobante" valor={t.registroComprobanteDias ? dias(t.registroComprobanteDias.mediana) : '—'}
                  detalle={t.registroComprobanteDias ? `Mediana · 9 de cada 10 en ${dias(t.registroComprobanteDias.p90)} o menos · ${pagos(t.registroComprobanteDias.n)}` : 'Sin pagos con comprobante en el periodo'} />
                <Metrica etiqueta="Pagados en su fecha" valor={t.puntualidad.aTiempoPct == null ? '—' : `${t.puntualidad.aTiempoPct}%`}
                  detalle={t.puntualidad.pagosConFechaProgramada
                    ? `${pagos(t.puntualidad.pagosConFechaProgramada)} con fecha programada${t.puntualidad.atrasoDias ? ` · los tardíos, ${dias(t.puntualidad.atrasoDias.mediana)} de atraso (mediana)` : ''}`
                    : 'Sin pagos con fecha programada'} />
                <Metrica etiqueta="Pagados con comprobante" valor={t.cerradasPct == null ? '—' : `${t.cerradasPct}%`} detalle="De lo pagado en el periodo" />
              </div>
              <p style={{ fontSize: 12, color: 'var(--glass)', margin: '14px 0 0' }}>
                Hasta el 1 de octubre de 2026 la fecha de pago se llenaba sola con el día de captura, así que en esos pagos "del pago al comprobante" tiende a salir en 0 días.
              </p>
            </div>
          )}

          {/* ── Avisos al solicitante que no salieron ── */}
          {data.avisosFallidos?.length > 0 && (
            <div style={{ ...css.card, padding: '16px 20px', border: '1px solid rgba(180, 83, 9, .35)' }}>
              <div style={{ fontFamily: 'var(--label-font)', fontSize: 15, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}><span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--st-pendiente)', marginRight: 8, verticalAlign: 'middle' }} />Avisos de pago que no se enviaron</div>
              <div style={{ fontSize: 12.5, color: 'var(--glass)', marginBottom: 12 }}>Al marcar un pago, el solicitante recibe un correo con el comprobante. Estos no salieron en los últimos 30 días.</div>
              {data.avisosFallidos.map(a => (
                <div key={`${a.coleccion}-${a.id}`} style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--mesh-soft)', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontFamily: 'var(--digit-font)', fontSize: 12, color: 'var(--plasma)' }}>{a.folio}</span>
                    <span style={{ fontSize: 13, marginLeft: 10 }}>{a.solicitante || 'Solicitante'}{a.email ? ` · ${a.email}` : ''}</span>
                    <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2 }}>{a.error}</div>
                  </div>
                  <button type="button" onClick={() => reenviar(a)} disabled={reenviando === a.id} style={{ ...css.btnGhost, fontSize: 12.5 }}>
                    {reenviando === a.id ? 'Enviando…' : 'Reenviar aviso'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Modal de acción ── */}
      <Modal open={!!modal} onClose={() => !modal?.enviando && setModal(null)} title={meta?.accion || ''} width={500}>
        {modal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
              <span style={{ fontFamily: 'var(--digit-font)', color: 'var(--plasma)' }}>{modal.item.folio}</span> · {modal.item.empresa} · {modal.item.proveedor || '—'} ·{' '}
              <span style={{ fontFamily: 'var(--digit-font)', color: 'var(--ink)' }}>{fmt(modal.item.montoMXN)}</span>
            </div>
            {modal.item.caso !== 'comprobante_sin_marcar' && (
              <div>
                <Label required>Comprobante de pago</Label>
                <input type="file" accept=".pdf,image/*" multiple
                  onChange={e => { const files = [...e.target.files]; setModal(m => ({ ...m, files })); }}
                  style={{ fontSize: 13 }} />
                <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>PDF o imagen de la transferencia. Se guarda con el concepto "Comprobante de pago".</div>
              </div>
            )}
            {modal.item.estado === 'por_pagar' && (
              <SelectorCuentaPago empresa={modal.item.empresa} value={modal.cuentaPago} onChange={v => setModal(m => ({ ...m, cuentaPago: v }))} />
            )}
            {modal.item.estado === 'por_pagar' && (
              <div>
                <Label required>Fecha de pago</Label>
                <input type="date" value={modal.fecha} max={hoyISO()} required
                  onChange={e => setModal(m => ({ ...m, fecha: e.target.value }))} style={{ ...css.input(false), maxWidth: 220 }} />
                <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>La fecha en que salió el pago del banco, no la de hoy.</div>
              </div>
            )}
            {modal.item.estado === 'por_pagar' && (
              <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', background: 'var(--panel-hi)', border: '1px solid var(--mesh-soft)', borderRadius: 4, padding: '8px 12px' }}>
                Al marcarla pagada, el solicitante recibe un correo con la confirmación y el comprobante.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setModal(null)} disabled={modal.enviando} style={css.btnGhost}>Cancelar</button>
              <button type="button" onClick={confirmar} disabled={modal.enviando} style={{ ...css.btn('#ff541c'), opacity: modal.enviando ? 0.7 : 1 }}>
                {modal.enviando ? 'Guardando…' : meta?.accion}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Metrica({ etiqueta, valor, detalle }) {
  return (
    <div>
      <div className="nx-label" style={{ marginBottom: 6 }}>{etiqueta}</div>
      <div style={{ fontFamily: 'var(--digit-font)', fontSize: 24, color: 'var(--ink)' }}>{valor}</div>
      <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 4, lineHeight: 1.4 }}>{detalle}</div>
    </div>
  );
}
